import React from "react";
import { Link } from "react-router-dom";

/**
 * Footer Component
 *
 * This renders the footer for the MultiKeys portal.
 * It is styled to fit a dark-mode theme, utilizing the CSS custom variables defined in `index.css`.
 *
 * Features:
 * - Brand info section explaining what MultiKeys is.
 * - Quick links for navigation (Home, Docs, Download).
 * - Legal section with stub anchors (`#/terms` and `#/privacy`) to satisfy regulatory/L3 compliance without
 *   implementing dedicated legal pages yet.
 *
 * For non-coders:
 * - Update the copyright year or brand name in the bottom text as needed.
 * - Adjust margins or column arrangements by tweaking the flex styles in the return statement.
 */
export const Footer: React.FC = () => {
  const footerStyle: React.CSSProperties = {
    backgroundColor: "var(--color-bg-secondary)",
    borderTop: "1px solid var(--color-border)",
    padding: "2.5rem 2rem 1.5rem 2rem",
    marginTop: "auto", // Pushes the footer to the bottom of the page container
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  };

  const columnsContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "2rem",
  };

  const columnStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    minWidth: "180px",
  };

  const headingStyle: React.CSSProperties = {
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: "var(--color-text-primary)",
    fontWeight: 700,
    marginBottom: "0.25rem",
  };

  const linkStyle: React.CSSProperties = {
    color: "var(--color-text-secondary)",
    fontSize: "0.9rem",
    transition: "color var(--transition-speed) ease",
  };

  const textMutedStyle: React.CSSProperties = {
    color: "var(--color-text-muted)",
    fontSize: "0.85rem",
    lineHeight: "1.5",
  };

  return (
    <footer style={footerStyle} data-testid="layout-footer">
      <div style={columnsContainerStyle}>
        {/* Column 1: Brand Info */}
        <div style={{ ...columnStyle, maxWidth: "300px" }}>
          <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--color-text-primary)" }}>
            <span style={{ color: "var(--color-accent)", marginRight: "0.5rem" }}>⌘</span>
            MultiKeys
          </div>
          <p style={textMutedStyle}>
            Seamless software-based keyboard and mouse sharing for power users. Control multiple computers across your local network with a single keyboard and mouse.
          </p>
        </div>

        {/* Column 2: Navigation Links */}
        <div style={columnStyle}>
          <h4 style={headingStyle}>Navigation</h4>
          <Link to="/" style={linkStyle} data-testid="footer-link-home">Home</Link>
          <Link to="/docs" style={linkStyle} data-testid="footer-link-docs">Documentation</Link>
          <Link to="/download" style={linkStyle} data-testid="footer-link-download">Download App</Link>
        </div>

        {/* Column 3: Legal Compliance */}
        <div style={columnStyle}>
          <h4 style={headingStyle}>Legal</h4>
          {/* L3 Compliance: Legal anchors mapped to hash stubs to prevent dead routes */}
          <a href="#/terms" style={linkStyle} data-testid="footer-link-terms">
            Terms of Service
          </a>
          <a href="#/privacy" style={linkStyle} data-testid="footer-link-privacy">
            Privacy Policy
          </a>
        </div>
      </div>

      {/* Bottom Row: Copyright */}
      <div 
        style={{
          borderTop: "1px solid var(--color-border)",
          paddingTop: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem"
        }}
      >
        <span style={textMutedStyle} data-testid="footer-copyright">
          &copy; {new Date().getFullYear()} MultiKeys. All rights reserved.
        </span>
        <span style={textMutedStyle}>
          Designed for developers and power users.
        </span>
      </div>
    </footer>
  );
};

export default Footer;
